// GH-761 — "היומנים שלי ב-Google": the expert picks which of their Google calendars block availability.
// The primary calendar is always checked (and locked); everything else is opt-in. Nothing is required —
// an expert who never opens this keeps today's behaviour (primary only).
import { LockOutlined } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import constants from '../config/constants';
import { useLanguage } from '../context/language.context';
import useFetch from '../hooks/useFetch';

type CalendarRow = { id: string; name: string; primary: boolean; owner: boolean; selected: boolean };
type Payload = { enabled?: boolean; connected: boolean; calendars: CalendarRow[]; busy_calendar_ids: string[] };

const GoogleCalendarsPicker = () => {
  const { t, dir } = useLanguage();
  const url = `${constants.API_URL}/instructor/admin/google-calendars`;
  const { data, loading, refetch } = useFetch<Payload>(url);
  const [chosen, setChosen] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  useEffect(() => {
    if (data) setChosen(data.busy_calendar_ids || []);
  }, [data]);

  const dirty = useMemo(() => {
    const a = [...(data?.busy_calendar_ids || [])].sort().join('|');
    const b = [...chosen].sort().join('|');
    return a !== b;
  }, [data, chosen]);

  const toggle = (id: string) =>
    setChosen((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const save = async () => {
    setSaving(true);
    setStatus('idle');
    try {
      await axios.put(url, { ids: chosen });
      setStatus('saved');
      await refetch();
    } catch {
      setStatus('error');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) {
    return (
      <Box display='flex' justifyContent='center' p={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  // Hidden when the backend switch is off, or when Google isn't connected yet (the connect block above says what to do)
  if (data?.enabled === false || !data?.connected) return null;

  return (
    <Box dir={dir} display='flex' flexDirection='column' alignItems='center' gap={2} p={3} maxWidth={600} mx='auto'>
      <Typography fontWeight={600} textAlign='center'>
        {t('busyCalendarsTitle')}
      </Typography>
      <Typography fontSize={14} textAlign='center' color='text.secondary'>
        {t('busyCalendarsDescription')}
      </Typography>
      <Stack width='100%' sx={{ textAlign: 'start' }}>
        {data.calendars.map((c) => (
          <FormControlLabel
            key={c.id}
            control={
              <Checkbox
                checked={c.primary || chosen.includes(c.id)}
                disabled={c.primary || saving}
                onChange={() => toggle(c.id)}
              />
            }
            label={
              <Box display='flex' alignItems='center' gap={1}>
                <span>{c.primary ? t('busyCalendarsPrimaryName') : c.name}</span>
                {c.primary && (
                  <Typography component='span' fontSize={12} color='text.secondary' display='inline-flex' alignItems='center' gap={0.5}>
                    <LockOutlined fontSize='inherit' /> {t('busyCalendarsPrimary')}
                  </Typography>
                )}
              </Box>
            }
          />
        ))}
      </Stack>
      <Button variant='contained' onClick={save} disabled={!dirty || saving}>
        {saving ? <CircularProgress size={18} color='inherit' /> : t('save')}
      </Button>
      {status === 'saved' && <Alert severity='success'>{t('busyCalendarsSaved')}</Alert>}
      {status === 'error' && <Alert severity='error'>{t('busyCalendarsError')}</Alert>}
    </Box>
  );
};

export default GoogleCalendarsPicker;
